import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ArrowRight } from 'lucide-react';
import { DecryptedText } from './animations/DecryptedText';
import { StarBorder } from './animations/StarBorder';

export const HeroSection = () => {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const scrollToContent = () => {
    const element = document.getElementById('intro-section');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden">
      {/* Dark Background */}
      <div className="absolute inset-0 bg-black">
        <div className="absolute inset-0 bg-gradient-to-br from-zinc-900 via-black to-black"></div>
      </div>

      {/* Logo Image as Background - Responsive */}
      <div 
        className="absolute inset-0 bg-center bg-no-repeat opacity-40"
        style={{
          backgroundImage: 'url(https://customer-assets.emergentagent.com/job_eb3e5c94-2f85-4e1f-91b8-a1b5d67e6202/artifacts/hz6a2tqw_Screenshot%202025-11-10%20111436.png)',
          backgroundSize: 'clamp(200px, 80%, 800px)',
          backgroundPosition: 'center',
        }}
      ></div>

      {/* Content */}
      <div className="container mx-auto px-4 sm:px-6 relative z-10">
        <div className="flex items-center min-h-screen py-20">
          <div
            className={`max-w-3xl transform transition-all duration-1000 ${
              isVisible ? 'translate-x-0 opacity-100' : '-translate-x-10 opacity-0'
            }`}
          >
            {/* Responsive Text Sizes */}
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-bold mb-4 sm:mb-6 text-white leading-tight">
              {t('hero.title1')} <span className="text-gray-500">{t('hero.title2')}</span>
            </h1>

            <p className="text-lg sm:text-xl md:text-2xl lg:text-3xl text-gray-400 mb-4 sm:mb-6 font-light">
              <DecryptedText text={t('hero.subtitle')} delay={500} />
            </p>

            <p className="text-base sm:text-lg md:text-xl text-gray-500 mb-8 sm:mb-10 max-w-2xl">
              <DecryptedText text={t('hero.description')} delay={1500} />
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <StarBorder onClick={scrollToContent}>
                <span className="text-sm sm:text-base">{t('hero.cta')}</span>
                <ArrowRight className="ml-2 h-4 w-4 sm:h-5 sm:w-5 inline" />
              </StarBorder>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator - Hidden on small mobile */}
      <button
        onClick={scrollToContent}
        className="absolute bottom-6 sm:bottom-10 left-1/2 transform -translate-x-1/2 animate-bounce cursor-pointer bg-transparent border-none hidden sm:block"
        aria-label="Scroll to content"
      >
        <ChevronDown className="h-8 w-8 sm:h-10 sm:w-10 text-gray-600" />
      </button>

      {/* Floating particles - Reduced on mobile */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(10)].map((_, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 bg-white/20 rounded-full animate-pulse hidden sm:block"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 3}s`,
              animationDuration: `${3 + Math.random() * 4}s`,
            }}
          ></div>
        ))}
      </div>
    </section>
  );
};